import { z } from "zod";
import { coordinatesSchema, timestampSchema } from "./common.js";
import { datasetRowStrategySchema, type DatasetRowStrategy } from "./asset.js";

export enum GlobalRole {
  ADMIN = "ADMIN",
  OPERATOR = "OPERATOR",
  VIEWER = "VIEWER",
  GUEST = "GUEST",
}

export const globalRoleSchema = z.nativeEnum(GlobalRole);

export enum UserStatus {
  ACTIVE = "ACTIVE",
  INVITED = "INVITED",
  SUSPENDED = "SUSPENDED",
  DISABLED = "DISABLED",
}

export const userStatusSchema = z.nativeEnum(UserStatus);

export enum ProjectRole {
  PROJECT_ADMIN = "PROJECT_ADMIN",
  OPERATOR = "OPERATOR",
  VIEWER = "VIEWER",
}

export const projectRoleSchema = z.nativeEnum(ProjectRole);

export enum ProjectStatus {
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
  DISABLED = "DISABLED",
}

export const projectStatusSchema = z.nativeEnum(ProjectStatus);

export enum AuthStatus {
  INITIALIZING = "INITIALIZING",
  UNAUTHENTICATED = "UNAUTHENTICATED",
  AUTHENTICATED = "AUTHENTICATED",
  PROFILE_LOADING = "PROFILE_LOADING",
  READY = "READY",
  ACCESS_DENIED = "ACCESS_DENIED",
  ERROR = "ERROR",
}

export const authStatusSchema = z.nativeEnum(AuthStatus);

export enum Permission {
  USER_MANAGE = "USER_MANAGE",
  PROJECT_CREATE = "PROJECT_CREATE",
  PROJECT_UPDATE = "PROJECT_UPDATE",
  PROJECT_DELETE = "PROJECT_DELETE",
  PROJECT_VIEW = "PROJECT_VIEW",
  MESSAGE_DEFINITION_VIEW = "MESSAGE_DEFINITION_VIEW",
  MESSAGE_DEFINITION_MANAGE = "MESSAGE_DEFINITION_MANAGE",
  MESSAGE_GENERATE = "MESSAGE_GENERATE",
  MESSAGE_ANALYZE = "MESSAGE_ANALYZE",
  SCENARIO_VIEW = "SCENARIO_VIEW",
  SCENARIO_MANAGE = "SCENARIO_MANAGE",
  SCENARIO_RUN = "SCENARIO_RUN",
  GIS_VIEW = "GIS_VIEW",
  MESSAGE_EVENT_VIEW = "MESSAGE_EVENT_VIEW",
  MESSAGE_EVENT_CREATE = "MESSAGE_EVENT_CREATE",
  REPORT_VIEW = "REPORT_VIEW",
  REPORT_CREATE = "REPORT_CREATE",
  SETTINGS_VIEW = "SETTINGS_VIEW",
  SETTINGS_MANAGE = "SETTINGS_MANAGE",
  INTEGRATION_VIEW = "INTEGRATION_VIEW",
  INTEGRATION_MANAGE = "INTEGRATION_MANAGE",
  DEMO_INTERACT = "DEMO_INTERACT",
}

export const permissionSchema = z.nativeEnum(Permission);

export interface UserProfile {
  [key: string]: unknown;
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  globalRole: GlobalRole;
  status: UserStatus;
  organization?: string;
  department?: string;
  phoneNumber?: string;
  defaultProjectId?: string;
  authProvider: "email" | "google" | "mock";
  environment: string;
  lastLoginAt?: string;
  membershipIds?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export const userProfileSchema: z.ZodSchema<UserProfile> = z
  .object({
    uid: z.string(),
    email: z.string().email(),
    displayName: z.string(),
    photoURL: z.string().optional(),
    globalRole: globalRoleSchema,
    status: userStatusSchema,
    organization: z.string().optional(),
    department: z.string().optional(),
    phoneNumber: z.string().optional(),
    defaultProjectId: z.string().optional(),
    authProvider: z.enum(["email", "google", "mock"]),
    environment: z.string(),
    lastLoginAt: z.string().datetime().optional(),
    membershipIds: z.array(z.string()).optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    createdBy: z.string().optional(),
  })
  .passthrough();

export interface Project {
  [key: string]: unknown;
  id: string;
  name: string;
  description: string;
  status: ProjectStatus;
  ownerId: string;
  environment: string;
  isDemo: boolean;
  standards: string[];
  vehicleIds: string[];
  defaultRouteId?: string;
  createdAt: string;
  updatedAt: string;
}

export const projectSchema: z.ZodSchema<Project> = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    status: projectStatusSchema,
    ownerId: z.string(),
    environment: z.string(),
    isDemo: z.boolean(),
    standards: z.array(z.string()),
    vehicleIds: z.array(z.string()),
    defaultRouteId: z.string().optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface ProjectMember {
  [key: string]: unknown;
  id: string;
  projectId: string;
  uid: string;
  email: string;
  displayName: string;
  projectRole: ProjectRole;
  status: UserStatus;
  joinedAt: string;
  createdAt: string;
  updatedAt: string;
}

export const projectMemberSchema: z.ZodSchema<ProjectMember> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    uid: z.string(),
    email: z.string().email(),
    displayName: z.string(),
    projectRole: projectRoleSchema,
    status: userStatusSchema,
    joinedAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface Waypoint {
  [key: string]: unknown;
  id: string;
  name: string;
  order: number;
  coordinates: { latitude: number; longitude: number; altitudeM: number };
  speedMps?: number;
  hoverTimeSec?: number;
  actions?: string[];
}

export const waypointSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    order: z.number().int(),
    coordinates: coordinatesSchema,
    speedMps: z.number().optional(),
    hoverTimeSec: z.number().optional(),
    actions: z.array(z.string()).optional(),
  })
  .passthrough();

export interface Route {
  [key: string]: unknown;
  id: string;
  projectId: string;
  name: string;
  description: string;
  waypoints: Waypoint[];
  totalDistanceM?: number;
  estimatedDurationSec?: number;
  environment: string;
  createdAt: string;
  updatedAt: string;
}

export const routeSchema: z.ZodSchema<Route> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    waypoints: z.array(waypointSchema),
    totalDistanceM: z.number().optional(),
    estimatedDurationSec: z.number().optional(),
    environment: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface VehicleState {
  [key: string]: unknown;
  id: string;
  projectId: string;
  vehicleId: string;
  callsign: string;
  coordinates: { latitude: number; longitude: number; altitudeM: number };
  headingDeg: number;
  speedMps: number;
  rollDeg?: number;
  pitchDeg?: number;
  yawDeg?: number;
  northVelocityMps?: number;
  eastVelocityMps?: number;
  verticalVelocityMps?: number;
  status: string;
  environment: string;
  updatedAt: string;
}

export const vehicleStateSchema: z.ZodSchema<VehicleState> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    vehicleId: z.string(),
    callsign: z.string(),
    coordinates: coordinatesSchema,
    headingDeg: z.number(),
    speedMps: z.number(),
    rollDeg: z.number().optional(),
    pitchDeg: z.number().optional(),
    yawDeg: z.number().optional(),
    northVelocityMps: z.number().optional(),
    eastVelocityMps: z.number().optional(),
    verticalVelocityMps: z.number().optional(),
    status: z.string(),
    environment: z.string(),
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface ScenarioEvent {
  [key: string]: unknown;
  id: string;
  scenarioId: string;
  type: string;
  messageType?: number;
  delayMs: number;
  payload?: Record<string, unknown>;
  repeat?: number;
}

export const scenarioEventSchema = z
  .object({
    id: z.string(),
    scenarioId: z.string(),
    type: z.string(),
    messageType: z.number().int().optional(),
    delayMs: z.number().int(),
    payload: z.record(z.unknown()).optional(),
    repeat: z.number().int().optional(),
  })
  .passthrough();

export interface Scenario {
  [key: string]: unknown;
  id: string;
  projectId: string;
  name: string;
  description: string;
  status: "idle" | "running" | "paused";
  events: ScenarioEvent[];
  vehicleIds: string[];
  routeId?: string;
  environment: string;
  createdAt: string;
  updatedAt: string;
}

export const scenarioSchema: z.ZodSchema<Scenario> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    status: z.enum(["idle", "running", "paused"]),
    events: z.array(scenarioEventSchema),
    vehicleIds: z.array(z.string()),
    routeId: z.string().optional(),
    environment: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface SimulationTest {
  [key: string]: unknown;
  id: string;
  projectId: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  environment: string;
  vehicleIds: string[];
  messageRules: MessageRule[];
  locationConfig: LocationConfig;
  executionConfig: ExecutionConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastExecutedAt?: string;
}

export const simulationTestSchema: z.ZodSchema<SimulationTest> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    tags: z.array(z.string()),
    environment: z.string(),
    vehicleIds: z.array(z.string()),
    messageRules: z.array(z.any()),
    locationConfig: z
      .object({
        defaultCoordinate: z
          .object({
            mode: z.enum(["CENTER_RADIUS", "BOUNDING_BOX", "ROUTE", "FIXED"]),
            centerLat: z.number().optional(),
            centerLon: z.number().optional(),
            radiusKm: z.number().optional(),
            minLat: z.number().optional(),
            maxLat: z.number().optional(),
            minLon: z.number().optional(),
            maxLon: z.number().optional(),
          })
          .passthrough(),
        coordinatePresets: z.array(z.any()).optional(),
      })
      .passthrough(),
    executionConfig: z
      .object({
        recordMode: z.enum(["NONE", "SUMMARY", "SAMPLE", "ALL"]),
        sampleRate: z.number().optional(),
        maxConcurrentRules: z.number().optional(),
        stopOnError: z.boolean().optional(),
        seed: z.string().optional(),
      })
      .passthrough(),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastExecutedAt: timestampSchema.optional(),
  })
  .passthrough();

export type GenerationMode =
  "SINGLE" | "PERIODIC" | "BULK" | "ROUTE_BASED" | "RANDOM";

export interface FieldGenerationRule {
  type:
    | "FIXED"
    | "RANDOM_RANGE"
    | "RANDOM_ENUM"
    | "RANDOM_COORDINATE"
    | "SEQUENCE"
    | "TIMESTAMP"
    | "EXPRESSION";
  value?: unknown;
  min?: number;
  max?: number;
  enumValues?: string[];
  coordinateConfig?: CoordinateConfig;
  step?: number;
  expression?: string;
}

export interface CoordinateConfig {
  mode: "CENTER_RADIUS" | "BOUNDING_BOX" | "ROUTE" | "FIXED";
  centerLat?: number;
  centerLon?: number;
  radiusKm?: number;
  minLat?: number;
  maxLat?: number;
  minLon?: number;
  maxLon?: number;
}

export interface LocationConfig {
  defaultCoordinate: CoordinateConfig;
  coordinatePresets?: CoordinateConfig[];
}

export interface ExecutionConfig {
  recordMode: "NONE" | "SUMMARY" | "SAMPLE" | "ALL";
  sampleRate?: number;
  maxConcurrentRules?: number;
  stopOnError?: boolean;
  seed?: string;
}

export interface MessageRule {
  id: string;
  messageType: number;
  messageDefinitionId?: string;
  enabled: boolean;
  sourceRole?: string;
  targetRole?: string;
  payloadTemplate: Record<string, unknown>;
  fieldRules: Record<string, FieldGenerationRule>;
  generationMode: GenerationMode;
  intervalMs: number;
  count: number | null;
  startDelayMs: number;
  randomization: boolean;
  faultInjection: boolean;
  order: number;
  // Prompt 14 Phase 2: asset reference support
  messageTemplateId?: string;
  parameterSetId?: string;
  parameterSetIds?: string[];
  datasetId?: string;
  datasetRowStrategy?: DatasetRowStrategy;
  runtimeOverrides?: Record<string, unknown>;
}

export const messageRuleSchema: z.ZodSchema<MessageRule> = z
  .object({
    id: z.string(),
    messageType: z.number().int(),
    messageDefinitionId: z.string().optional(),
    enabled: z.boolean(),
    sourceRole: z.string().optional(),
    targetRole: z.string().optional(),
    payloadTemplate: z.record(z.unknown()),
    fieldRules: z.record(
      z
        .object({
          type: z.enum([
            "FIXED",
            "RANDOM_RANGE",
            "RANDOM_ENUM",
            "RANDOM_COORDINATE",
            "SEQUENCE",
            "TIMESTAMP",
            "EXPRESSION",
          ]),
          value: z.unknown().optional(),
          min: z.number().optional(),
          max: z.number().optional(),
          enumValues: z.array(z.string()).optional(),
          coordinateConfig: z.any().optional(),
          step: z.number().optional(),
          expression: z.string().optional(),
        })
        .passthrough(),
    ),
    generationMode: z.enum([
      "SINGLE",
      "PERIODIC",
      "BULK",
      "ROUTE_BASED",
      "RANDOM",
    ]),
    intervalMs: z.number().int(),
    count: z.number().int().nullable(),
    startDelayMs: z.number().int(),
    randomization: z.boolean(),
    faultInjection: z.boolean(),
    order: z.number().int(),
    messageTemplateId: z.string().optional(),
    parameterSetId: z.string().optional(),
    parameterSetIds: z.array(z.string()).optional(),
    datasetId: z.string().optional(),
    datasetRowStrategy: datasetRowStrategySchema.optional(),
    runtimeOverrides: z.record(z.unknown()).optional(),
  })
  .passthrough();

export interface SimulationRun {
  [key: string]: unknown;
  id: string;
  simulationTestId: string;
  projectId: string;
  environment: string;
  name: string;
  status: "running" | "completed" | "stopped" | "error";
  startedAt: string;
  endedAt?: string;
  durationMs: number;
  generatedCount: number;
  sentCount: number;
  droppedCount: number;
  passCount: number;
  warnCount: number;
  failCount: number;
  messageStats: Record<
    number,
    {
      generated: number;
      sent: number;
      pass: number;
      warn: number;
      fail: number;
    }
  >;
  vehicleStats: Record<
    string,
    {
      messageCount: number;
      lastCoordinates?: {
        latitude: number;
        longitude: number;
        altitudeM: number;
      };
    }
  >;
  executionConfig: ExecutionConfig;
  metadata?: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const simulationRunSchema: z.ZodSchema<SimulationRun> = z
  .object({
    id: z.string(),
    simulationTestId: z.string(),
    projectId: z.string(),
    environment: z.string(),
    name: z.string(),
    status: z.enum(["running", "completed", "stopped", "error"]),
    startedAt: timestampSchema,
    endedAt: timestampSchema.optional(),
    durationMs: z.number().int(),
    generatedCount: z.number().int(),
    sentCount: z.number().int(),
    droppedCount: z.number().int(),
    passCount: z.number().int(),
    warnCount: z.number().int(),
    failCount: z.number().int(),
    messageStats: z.record(
      z.object({
        generated: z.number().int(),
        sent: z.number().int(),
        pass: z.number().int(),
        warn: z.number().int(),
        fail: z.number().int(),
      }),
    ),
    vehicleStats: z.record(
      z.object({
        messageCount: z.number().int(),
        lastCoordinates: coordinatesSchema.optional(),
      }),
    ),
    executionConfig: z
      .object({
        recordMode: z.enum(["NONE", "SUMMARY", "SAMPLE", "ALL"]),
        sampleRate: z.number().optional(),
        maxConcurrentRules: z.number().optional(),
        stopOnError: z.boolean().optional(),
        seed: z.string().optional(),
      })
      .passthrough(),
    metadata: z.record(z.unknown()).optional(),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export enum ReportType {
  SIMULATION_SUMMARY = "SIMULATION_SUMMARY",
  MESSAGE_ANALYSIS = "MESSAGE_ANALYSIS",
  EXECUTION_RESULT = "EXECUTION_RESULT",
}

export const reportTypeSchema = z.nativeEnum(ReportType);

export interface Report {
  [key: string]: unknown;
  id: string;
  projectId: string;
  simulationRunId: string;
  environment: string;
  type: ReportType;
  title: string;
  summary: {
    totalMessages: number;
    sentCount: number;
    droppedCount: number;
    passCount: number;
    warnCount: number;
    failCount: number;
    durationMs: number;
  };
  messageTypeStats: Record<
    number,
    { count: number; pass: number; warn: number; fail: number }
  >;
  roleErrors: {
    messageType: number;
    expected: string;
    actual: string;
    count: number;
  }[];
  fieldErrors: { messageType: number; field: string; count: number }[];
  timingErrors: { messageType: number; count: number }[];
  alerts: {
    severity: string;
    message: string;
    vehicleId?: string;
    timestamp: string;
  }[];
  vehicleSummary: Record<
    string,
    {
      messageCount: number;
      lastCoordinates?: {
        latitude: number;
        longitude: number;
        altitudeM: number;
      };
    }
  >;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const reportSchema: z.ZodSchema<Report> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    simulationRunId: z.string(),
    environment: z.string(),
    type: reportTypeSchema,
    title: z.string(),
    summary: z.object({
      totalMessages: z.number().int(),
      sentCount: z.number().int(),
      droppedCount: z.number().int(),
      passCount: z.number().int(),
      warnCount: z.number().int(),
      failCount: z.number().int(),
      durationMs: z.number().int(),
    }),
    messageTypeStats: z.record(
      z.object({
        count: z.number().int(),
        pass: z.number().int(),
        warn: z.number().int(),
        fail: z.number().int(),
      }),
    ),
    roleErrors: z.array(
      z.object({
        messageType: z.number().int(),
        expected: z.string(),
        actual: z.string(),
        count: z.number().int(),
      }),
    ),
    fieldErrors: z.array(
      z.object({
        messageType: z.number().int(),
        field: z.string(),
        count: z.number().int(),
      }),
    ),
    timingErrors: z.array(
      z.object({
        messageType: z.number().int(),
        count: z.number().int(),
      }),
    ),
    alerts: z.array(
      z.object({
        severity: z.string(),
        message: z.string(),
        vehicleId: z.string().optional(),
        timestamp: timestampSchema,
      }),
    ),
    vehicleSummary: z.record(
      z.object({
        messageCount: z.number().int(),
        lastCoordinates: coordinatesSchema.optional(),
      }),
    ),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface MessageEvent {
  [key: string]: unknown;
  id: string;
  projectId: string;
  runId?: string;
  messageDefinitionId?: string;
  messageType: number;
  standard: string;
  name: string;
  source: string;
  target: string;
  sourceRole?: string;
  targetRole?: string;
  direction: string;
  timestamp: string;
  payload?: Record<string, unknown>;
  fields: Record<string, unknown>;
  rawBytes?: number[];
  rawHex?: string;
  validation?: string;
  validationStatus?: string;
  validationResults?: Record<string, unknown>;
  sequenceNumber?: number;
  createdBy?: string;
  metadata?: Record<string, unknown>;
  environment: string;
  createdAt: string;
}

export const messageEventSchema: z.ZodSchema<MessageEvent> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    runId: z.string().optional(),
    messageDefinitionId: z.string().optional(),
    messageType: z.number().int(),
    standard: z.string(),
    name: z.string(),
    source: z.string(),
    target: z.string(),
    sourceRole: z.string().optional(),
    targetRole: z.string().optional(),
    direction: z.string(),
    timestamp: timestampSchema,
    payload: z.record(z.unknown()).optional(),
    fields: z.record(z.unknown()),
    rawBytes: z.array(z.number()).optional(),
    rawHex: z.string().optional(),
    validation: z.string().optional(),
    validationStatus: z.string().optional(),
    validationResults: z.record(z.unknown()).optional(),
    sequenceNumber: z.number().int().optional(),
    createdBy: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    environment: z.string(),
    createdAt: timestampSchema,
  })
  .passthrough();

export enum AdapterType {
  VIRTUAL = "VIRTUAL",
  REPLAY = "REPLAY",
  WEBSOCKET = "WEBSOCKET",
  LOCAL_BRIDGE = "LOCAL_BRIDGE",
  UDP = "UDP",
  UDP_MULTICAST = "UDP_MULTICAST",
  TCP_CLIENT = "TCP_CLIENT",
  TCP_SERVER = "TCP_SERVER",
}

export const adapterTypeSchema = z.nativeEnum(AdapterType);

export type IntegrationDirection = "INBOUND" | "OUTBOUND" | "BIDIRECTIONAL";

export const integrationDirectionSchema = z.enum([
  "INBOUND",
  "OUTBOUND",
  "BIDIRECTIONAL",
]);

export type AdapterStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "error";

export const adapterStatusSchema = z.enum([
  "idle",
  "connecting",
  "connected",
  "reconnecting",
  "disconnected",
  "error",
]);

export interface ConnectionProfile {
  [key: string]: unknown;
  id: string;
  projectId: string;
  name: string;
  description: string;
  adapterType: AdapterType;
  enabled: boolean;
  direction: IntegrationDirection;
  configuration: Record<string, unknown>;
  autoConnect: boolean;
  reconnectPolicy: {
    enabled: boolean;
    intervalMs: number;
    maxRetries: number;
  };
  environment: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const connectionProfileSchema: z.ZodSchema<ConnectionProfile> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    adapterType: adapterTypeSchema,
    enabled: z.boolean(),
    direction: integrationDirectionSchema,
    configuration: z.record(z.unknown()),
    autoConnect: z.boolean(),
    reconnectPolicy: z.object({
      enabled: z.boolean(),
      intervalMs: z.number().int(),
      maxRetries: z.number().int(),
    }),
    environment: z.string(),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface PacketEntry {
  id: string;
  timestamp: string;
  direction: "RX" | "TX";
  adapterName: string;
  adapterType: AdapterType;
  source?: string;
  target?: string;
  messageType?: number;
  messageName?: string;
  sizeBytes: number;
  status: "ok" | "warn" | "error";
  raw?: string;
  metadata?: Record<string, unknown>;
}

export const packetEntrySchema: z.ZodSchema<PacketEntry> = z
  .object({
    id: z.string(),
    timestamp: timestampSchema,
    direction: z.enum(["RX", "TX"]),
    adapterName: z.string(),
    adapterType: adapterTypeSchema,
    source: z.string().optional(),
    target: z.string().optional(),
    messageType: z.number().int().optional(),
    messageName: z.string().optional(),
    sizeBytes: z.number().int(),
    status: z.enum(["ok", "warn", "error"]),
    raw: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

export interface IntegrationStats {
  rxCount: number;
  txCount: number;
  rxBytes: number;
  txBytes: number;
  errors: number;
  reconnects: number;
  lastError?: string;
  lastConnectedAt?: string;
  lastDisconnectedAt?: string;
}

export type AdapterStats = IntegrationStats;

export const integrationStatsSchema: z.ZodSchema<IntegrationStats> = z
  .object({
    rxCount: z.number().int(),
    txCount: z.number().int(),
    rxBytes: z.number().int(),
    txBytes: z.number().int(),
    errors: z.number().int(),
    reconnects: z.number().int(),
    lastError: z.string().optional(),
    lastConnectedAt: timestampSchema.optional(),
    lastDisconnectedAt: timestampSchema.optional(),
  })
  .passthrough();

// Test Asset & Verdict Models (Prompt 10)

export type PassCriterionType =
  | "NO_FAIL"
  | "MAX_WARN_COUNT"
  | "MIN_PASS_RATE"
  | "EXPECTED_MESSAGE_COUNT"
  | "EXPECTED_MESSAGE_TYPE"
  | "FIELD_RANGE"
  | "ROLE_MATCH"
  | "TIMING_WITHIN"
  | "NO_MISSING_MESSAGE"
  | "CUSTOM_EXPRESSION";

export interface PassCriterion {
  type: PassCriterionType;
  params: Record<string, unknown>;
}

export interface TestCase {
  [key: string]: unknown;
  id: string;
  suiteId: string;
  projectId: string;
  name: string;
  description: string;
  objective: string;
  preconditions: string[];
  messageRules: MessageRule[];
  expectedResults: string[];
  passCriteria: PassCriterion[];
  timeoutMs: number;
  executionOrder: number;
  enabled: boolean;
  tags: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const testCaseSchema: z.ZodSchema<TestCase> = z
  .object({
    id: z.string(),
    suiteId: z.string(),
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    objective: z.string(),
    preconditions: z.array(z.string()),
    messageRules: z.array(z.any()),
    expectedResults: z.array(z.string()),
    passCriteria: z.array(
      z.object({
        type: z.enum([
          "NO_FAIL",
          "MAX_WARN_COUNT",
          "MIN_PASS_RATE",
          "EXPECTED_MESSAGE_COUNT",
          "EXPECTED_MESSAGE_TYPE",
          "FIELD_RANGE",
          "ROLE_MATCH",
          "TIMING_WITHIN",
          "NO_MISSING_MESSAGE",
          "CUSTOM_EXPRESSION",
        ]),
        params: z.record(z.unknown()),
      }),
    ),
    timeoutMs: z.number().int(),
    executionOrder: z.number().int(),
    enabled: z.boolean(),
    tags: z.array(z.string()),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .passthrough();

export interface TestSuite {
  [key: string]: unknown;
  id: string;
  projectId: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  version: string;
  testCaseIds: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastExecutedAt?: string;
}

export const testSuiteSchema: z.ZodSchema<TestSuite> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    tags: z.array(z.string()),
    version: z.string(),
    testCaseIds: z.array(z.string()),
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastExecutedAt: timestampSchema.optional(),
  })
  .passthrough();

export type TestVerdictStatus = "PASS" | "FAIL" | "INCONCLUSIVE";

export interface TestVerdictEvidence {
  messageId?: string;
  timestamp?: string;
  messageType?: number;
  field?: string;
  expected?: unknown;
  actual?: unknown;
  issue?: string;
}

export interface TestCaseVerdict {
  testCaseId: string;
  testCaseName: string;
  status: TestVerdictStatus;
  evidence: TestVerdictEvidence[];
  durationMs: number;
  generatedCount: number;
  passCount: number;
  warnCount: number;
  failCount: number;
}

export interface SuiteResult {
  [key: string]: unknown;
  id: string;
  suiteId: string;
  runId: string;
  projectId: string;
  environment: string;
  verdicts: TestCaseVerdict[];
  summary: {
    totalCases: number;
    passCount: number;
    failCount: number;
    inconclusiveCount: number;
    generatedCount: number;
  };
  createdAt: string;
  createdBy: string;
}

export const suiteResultSchema: z.ZodSchema<SuiteResult> = z
  .object({
    id: z.string(),
    suiteId: z.string(),
    runId: z.string(),
    projectId: z.string(),
    environment: z.string(),
    verdicts: z.array(
      z.object({
        testCaseId: z.string(),
        testCaseName: z.string(),
        status: z.enum(["PASS", "FAIL", "INCONCLUSIVE"]),
        evidence: z.array(
          z
            .object({
              messageId: z.string().optional(),
              timestamp: z.string().optional(),
              messageType: z.number().int().optional(),
              field: z.string().optional(),
              expected: z.unknown().optional(),
              actual: z.unknown().optional(),
              issue: z.string().optional(),
            })
            .passthrough(),
        ),
        durationMs: z.number().int(),
        generatedCount: z.number().int(),
        passCount: z.number().int(),
        warnCount: z.number().int(),
        failCount: z.number().int(),
      }),
    ),
    summary: z.object({
      totalCases: z.number().int(),
      passCount: z.number().int(),
      failCount: z.number().int(),
      inconclusiveCount: z.number().int(),
      generatedCount: z.number().int(),
    }),
    createdAt: timestampSchema,
    createdBy: z.string(),
  })
  .passthrough();

export type AssetType =
  | "SIMULATION_TEST"
  | "TEST_SUITE"
  | "TEST_CASE"
  | "REPORT"
  | "CONNECTION_PROFILE"
  | "MESSAGE_TEMPLATE"
  | "PARAMETER_SET"
  | "DATASET"
  | "SESSION";

export const assetTypeSchema = z.enum([
  "SIMULATION_TEST",
  "TEST_SUITE",
  "TEST_CASE",
  "REPORT",
  "CONNECTION_PROFILE",
  "MESSAGE_TEMPLATE",
  "PARAMETER_SET",
  "DATASET",
  "SESSION",
]);

export interface Favorite {
  [key: string]: unknown;
  id: string;
  uid: string;
  assetType: AssetType;
  assetId: string;
  projectId?: string;
  createdAt: string;
}

export const favoriteSchema: z.ZodSchema<Favorite> = z
  .object({
    id: z.string(),
    uid: z.string(),
    assetType: assetTypeSchema,
    assetId: z.string(),
    projectId: z.string().optional(),
    createdAt: timestampSchema,
  })
  .passthrough();
